import { Injectable, Optional, inject } from '@angular/core';

import { AccaBoostConfig, BetslipConfig, CrmConfig, UKGCConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { CalculatedOdds, OddsOperations, emptyCalculatedOdds, emptyOdds } from '@frontend/sports/odds/feature';
import { AccaBoostCalculations } from '@frontend/sports/types/components/acca-boost';
import { SyncUserSettingsService, UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { Decimal } from 'decimal.js';
import { TokensCalculatorService } from 'packages/sports/web/app/src/tokens-base/tokens-calculator.service';
import { Observable, map, of } from 'rxjs';

import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { V1PickId } from '../../../core/picks/pick-id';
import { PickType, PriceType } from '../../../core/picks/pick-models';
import { isBetBuilderPick } from '../../../core/utils';
import { IBet, IBetslip, ICalculatedPrice, ISlip } from '../../bet-generation/models';
import { isComboSlipId } from '../../bet-generation/services/slip.utils';
import { SingleSlipType } from '../../bet-generation/slip-type';
import { AccaBoostToken } from '../../reward-tokens/reward-tokens.model';
import { isBetStationFreebetToken } from '../../reward-tokens/services/betstation-reward-tokens.utils';
import {
    getAccaBoostRatio,
    isAccaBoostToken,
    isFreebetToken,
    isOddsBoostToken,
    isRiskFreeToken,
} from '../../reward-tokens/services/reward-tokens.utils';
import BetslipTaxationService from '../../taxation/services/betslip-taxation.service';
import { MINIMUM_BET_BUILDER_SELECTIONS } from '../../validation/services/validators/betslip/min-selections-betbuilder.validator';
import { IAccaBoostModification, IBetslipSummary, IRewardTokensModifications, ISlipSummary, emptyBetslipSummary, emptySlipSummary } from '../models';
import { getOddsBoostWinningsBoost } from '../utils/betslip-summary.utils';
import { getSlipTotalStakeAndBetCount } from './betslip-summary-helpers';

export function getPossibleWinningsNet(
    winnings: Decimal | null,
    taxation: Decimal | null = null,
    winningsBoost: Decimal | null = null,
): Decimal | null {
    return winnings ? winnings.minus(taxation || 0).plus(winningsBoost || 0) : null;
}

@Injectable()
export default class BetslipSummaryBuilder {
    isTurnoverTax: boolean;
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        private betslipConfig: BetslipConfig,
        private tokensCalculator: TokensCalculatorService,
        private taxationService: BetslipTaxationService,
        private userSettings: SyncUserSettingsService,
        private crmConfig: CrmConfig,
        @Optional() private accaBoostConfig: AccaBoostConfig,
        private uKGCConfig: UKGCConfig,
        private nativeAppService: NativeAppService,
    ) {
        this.isTurnoverTax = this.userSettings.isTurnoverTaxApplied;
    }

    createSlipSummary(slip: ISlip): ISlipSummary {
        if (slip.bets.length === 0) {
            return emptySlipSummary;
        }

        const summary = { ...emptySlipSummary };

        // Get total num of bets in a slip.
        summary.slip = slip;
        const { totalStake, betCount } = getSlipTotalStakeAndBetCount(slip);
        summary.betCount = betCount;

        // Total stake
        summary.totalStake = totalStake;
        // Total odds for the slip is the sum of the odds of all bets. If EW, odds of the normal bet
        summary.totalOdds = OddsOperations.sum(slip.bets.map((b) => this.getBetTotalOdds(b)));

        // In case of combo EW we also want to store the regular odds
        if (isComboSlipId(slip.slipId) && slip.bets.length === 2) {
            summary.baseTotalOdds = this.getBetTotalOdds(slip.bets[0]);
        }

        summary.hasXCastPick = Object.values(slip.slipPicks).some((pick) => pick.isXCast);
        summary.hasStartingPrice = Object.values(slip.slipPicks).some((pick) => pick.isStartingPrice);
        // Reward tokens
        if (slip.rewardToken) {
            const token = slip.rewardToken;
            if (isFreebetToken(token) || isBetStationFreebetToken(token)) {
                summary.rewardTokenModifications = {
                    ...summary.rewardTokenModifications,
                    freeBet: {
                        amount: new Decimal(token.amount),
                    },
                };
            } else if (isRiskFreeToken(token) && slip.stake && summary.betCount === 1) {
                // Risk free can be assigned only for one bet.
                const stake = slip.stake; // Since the betCount is only 1 then totalStake is the slip stake.
                summary.rewardTokenModifications = {
                    ...summary.rewardTokenModifications,
                    riskFree: {
                        amount: new Decimal(this.tokensCalculator.getTokenPercentageReturns(token.maximumPayout, token.riskFreePercentage, stake)),
                    },
                };
            } else if (isOddsBoostToken(token) && summary.betCount === 1) {
                // Odds boost can be applied only when only one bet.
                if (summary.totalOdds && !OddsOperations.isOddsEmpty(summary.totalOdds) && token.boostFactor) {
                    const boostedOdds = this.tokensCalculator.getBoostedNativeOdds2(
                        summary.totalOdds,
                        token.boostFactor,
                        token.oddsBoostFormula,
                        this.userOddsFormatSettingsService.oddsFormat,
                    )!;
                    const boostedWinnings = summary.totalStake
                        ? this.roundNumber(
                              OddsOperations.toDecimalValue(boostedOdds, this.userOddsFormatSettingsService.oddsFormat).times(summary.totalStake),
                          )
                        : new Decimal(0);

                    summary.rewardTokenModifications = {
                        ...summary.rewardTokenModifications,
                        oddsBoost: {
                            boostedOdds,
                            boostedWinnings,
                        },
                    };
                }
            } else if (isAccaBoostToken(token) && summary.betCount === 1) {
                summary.rewardTokenModifications = {
                    ...summary.rewardTokenModifications,
                    accaBoost: this.getAccaBoostModification(token, slip),
                };
            }
        }

        summary.betSummaries = slip.bets.map((bet) => {
            return {
                bet,
                possibleWinnings: this.getBetWinningsStrategy(
                    bet,
                    this.isTurnoverTax ? this.getEffectiveStake(slip.stake) : slip.stake,
                    summary.rewardTokenModifications,
                ),
                freeBetWinnings:
                    this.isTurnoverTax && !!summary.rewardTokenModifications?.freeBet?.amount && !!slip.stake
                        ? this.getPossibleWins(bet, this.getEffectiveStake(slip.stake), summary.rewardTokenModifications?.freeBet?.amount || null)
                        : null,
            };
        });

        // Possible winnings
        summary.possibleWinnings = summary.betSummaries.reduce((winnings: Nullable<Decimal>, betSummary) => {
            if (!betSummary.possibleWinnings) {
                return winnings;
            }

            return winnings === null ? betSummary.possibleWinnings : winnings.plus(betSummary.possibleWinnings);
        }, null);

        return summary;
    }

    getAccaBoostModification(token: AccaBoostToken, slip: ISlip): IAccaBoostModification {
        const possibleWinnings = this.getBetWinnings(slip.bets[0], slip.stake, this.betslipConfig.roundValues);
        let currentBoostValue = new Decimal(0);
        let boostedWinningsValue = new Decimal(0);
        let boostRatio = new Decimal(0);
        let maxBoost = new Decimal(0);

        if (possibleWinnings) {
            boostRatio = new Decimal(getAccaBoostRatio(token, Object.keys(slip.slipPicks).length));
            maxBoost = new Decimal(token.maxReward);
            let boostedReturn = this.tokensCalculator.getAccaBoostReturns(possibleWinnings, slip.stake!, maxBoost, boostRatio);
            if (this.accaBoostConfig?.accaBoostCalculation === AccaBoostCalculations.Profit) {
                boostedReturn = this.roundNumber(boostedReturn);
                boostedWinningsValue = boostedReturn.plus(possibleWinnings);
                currentBoostValue = boostedReturn;
            } else {
                boostedWinningsValue = this.roundNumber(boostedReturn.plus(possibleWinnings));
                currentBoostValue = this.roundNumber(boostedReturn.plus(possibleWinnings)).minus(this.roundNumber(possibleWinnings));
            }
        }

        const accaboost = {
            boostedWinnings: boostedWinningsValue,
            originalWinnings: possibleWinnings ? this.roundNumber(possibleWinnings) : new Decimal(0),
            boostRatio,
            maxBoost: this.roundNumber(maxBoost),
            currentBoost: currentBoostValue,
        };

        return accaboost;
    }

    createBetslipSummaryWithoutTaxation(betslip: IBetslip): Omit<IBetslipSummary, 'taxation' | 'possibleWinningsNet'> {
        if (betslip.slips.length === 0) {
            return emptyBetslipSummary;
        }
        const summary: Omit<IBetslipSummary, 'taxation' | 'possibleWinningsNet'> = {
            slipSummaries: [],
            betCount: 0,
            hasBetBuilder: false,
            missingBetBuilderLegs: false,
            possibleWinnings: new Decimal(0),
            totalStake: new Decimal(0),
            totalOdds: emptyCalculatedOdds,
            teaserTotalOdds: emptyOdds,
            averageOdds: emptyCalculatedOdds,
            isPotentailReturn: false,
        };
        // For the possible winnings and total stake is the sum of pw/ts of all slips, but if all of them are null then the sum is null.
        let hasPossibleWinnings = false;
        let hasTotalStake = false;
        let hasXCastPick = false;
        let hasStartingPrice = false;
        const fixtureIds: string[] = [];
        for (const slip of betslip.slips) {
            const slipSummary = this.createSlipSummary(slip);
            summary.totalOdds = slipSummary.totalOdds;
            summary.baseTotalOdds = slipSummary.baseTotalOdds;
            summary.slipSummaries.push(slipSummary);
            summary.betCount += slipSummary.betCount;
            if (!hasPossibleWinnings && slipSummary.possibleWinnings !== null) {
                hasPossibleWinnings = true;
            }
            if (!hasTotalStake && slipSummary.totalStake !== null) {
                hasTotalStake = true;
            }
            if (slipSummary.hasXCastPick) {
                hasXCastPick = true;
            }
            if (slipSummary.hasStartingPrice) {
                hasStartingPrice = true;
            }
            const slipPossibleWinnings = slipSummary.possibleWinnings ? slipSummary.possibleWinnings : new Decimal(0);
            summary.possibleWinnings = summary.possibleWinnings ? summary.possibleWinnings.plus(slipPossibleWinnings) : null;
            summary.totalStake = summary.totalStake!.plus(slipSummary.totalStake || new Decimal(0));

            const slipPicks = Object.values(slip.slipPicks);
            slipPicks.forEach(({ pick }) => {
                if (pick.id.getPickType() === PickType.V1Pick) {
                    fixtureIds.push((pick.id as V1PickId)['eventId']);
                } else if (isBetBuilderPick(pick)) {
                    fixtureIds.push(pick.eventId);
                    summary.hasBetBuilder = true;
                    const options = BetslipBetBuilderPick.isPick(pick) && pick.sportcastOptions;
                    summary.missingBetBuilderLegs = summary.missingBetBuilderLegs || (options && options.length < MINIMUM_BET_BUILDER_SELECTIONS);
                } else {
                    fixtureIds.push(pick.id.fixtureId);
                }
            });
        }

        if (summary.slipSummaries.every((s) => s.totalOdds.decimals.greaterThan(0))) {
            const average = summary.slipSummaries
                .reduce((to, b) => to.add(b.totalOdds.decimals), new Decimal(0))
                .dividedBy(summary.slipSummaries.length);
            summary.averageOdds = average.gt(0) ? OddsOperations.fromDecimalValue(average) : emptyCalculatedOdds;
        }

        if (!hasPossibleWinnings || hasXCastPick || hasStartingPrice) {
            summary.possibleWinnings = null;
        }
        if (!hasTotalStake) {
            summary.totalStake = null;
        }
        const duplicateFixtureIds = fixtureIds.filter((currentValue: any, currentIndex: any) => fixtureIds.indexOf(currentValue) !== currentIndex);
        if (
            this.uKGCConfig.isPotentialReturnsEnabledForSingle &&
            (betslip.slips[0].type instanceof SingleSlipType || this.nativeAppService.isTerminal) &&
            duplicateFixtureIds.length &&
            !hasStartingPrice &&
            !hasXCastPick
        ) {
            summary.isPotentailReturn = true;
        }

        return summary;
    }

    createBetslipSummary(betslip: IBetslip): Observable<IBetslipSummary> {
        const noTaxationSummary = this.createBetslipSummaryWithoutTaxation(betslip);
        if (noTaxationSummary === emptyBetslipSummary) {
            return of(emptyBetslipSummary);
        }
        const summary: IBetslipSummary = {
            ...noTaxationSummary,
            possibleWinningsNet: new Decimal(0),
            taxation: null,
        };

        return this.taxationService.getTaxation(summary).pipe(
            map((taxation) => {
                let taxSum = new Decimal(0);
                const summaryTaxation = summary.slipSummaries.reduce((tax, slipSummary, i) => {
                    const slipSummaryTaxation = taxation && taxation[i];
                    slipSummary.taxation = slipSummaryTaxation || null;
                    taxSum = taxSum.plus(slipSummaryTaxation?.amount || new Decimal(0));

                    return { ...slipSummaryTaxation, amount: taxSum };
                }, {});
                summary.taxation = taxation ? summaryTaxation : null;
                const possibleWinnings = this.taxationService.getPossibleWinnings(summary);
                const winningsBoost = getOddsBoostWinningsBoost(summary);

                summary.possibleWinningsNet = getPossibleWinningsNet(possibleWinnings, taxSum, winningsBoost);

                return summary;
            }),
        );
    }

    private roundNumber(val: Decimal): Decimal {
        const round = this.betslipConfig.roundValues;

        return round ? val.times(100).round().div(100) : val.times(100).floor().div(100);
    }

    private multiplyListOfPrices(prices: ICalculatedPrice[]): CalculatedOdds {
        if (prices.length === 0) {
            return emptyCalculatedOdds;
        }
        if (prices.every((p) => p.type === PriceType.Fixed)) {
            return OddsOperations.multiply(
                prices.map((p) => p.odds),
                this.betslipConfig.roundPriceValues,
            );
        }

        return emptyCalculatedOdds;
    }

    private getBetTotalOdds(bet: IBet): CalculatedOdds {
        const prices = bet.legs.map((l) => l.price);

        return this.multiplyListOfPrices(prices);
    }

    private getBetWinnings(bet: IBet, stake: number | null, applyRounding: boolean = true): Nullable<Decimal> {
        if (!stake || !bet.isValid) {
            return null;
        }

        const betTotalOdds = this.getBetTotalOdds(bet);
        if (OddsOperations.isOddsEmpty(betTotalOdds)) {
            return null;
        }

        const decimalOdds = OddsOperations.toDecimalValue(betTotalOdds, this.userOddsFormatSettingsService.oddsFormat);
        const possibleWinnings = applyRounding ? this.roundNumber(decimalOdds.times(stake)) : decimalOdds.times(stake);

        return possibleWinnings;
    }

    private getFreeBetWinnings(bet: IBet, freeBetAmount: Decimal): Nullable<Decimal> {
        const effectiveFreeAmount = this.getEffectiveStake(Number(freeBetAmount));
        const possibleWinnings = this.getBetWinnings(bet, effectiveFreeAmount);

        if (!possibleWinnings) {
            return null;
        }

        const turnoverAndWinningTax = this.isTurnoverTax && this.userSettings.configSettings.winningsTaxThreshold <= Number(possibleWinnings);

        if (!this.crmConfig.tokens.useFreebetStakeInCalculations && !turnoverAndWinningTax) {
            return possibleWinnings.minus(freeBetAmount);
        }

        return possibleWinnings;
    }

    private getBetWinningsStrategy(bet: IBet, stake: number | null, rewardTokenModifications?: IRewardTokensModifications): Nullable<Decimal> {
        if (rewardTokenModifications?.accaBoost) {
            return rewardTokenModifications.accaBoost.boostedWinnings;
        }

        if (rewardTokenModifications?.freeBet) {
            return this.getFreeBetWinnings(bet, rewardTokenModifications.freeBet.amount);
        }

        return this.getBetWinnings(bet, stake);
    }

    private getPossibleWins(bet: IBet, stake: number | null, freeBetAmount: Nullable<Decimal>): Nullable<Decimal> {
        if (stake) {
            const betTotalOdds = this.getBetTotalOdds(bet);
            if (!OddsOperations.isOddsEmpty(betTotalOdds)) {
                const decimalOdds = OddsOperations.toDecimalValue(betTotalOdds, this.userOddsFormatSettingsService.oddsFormat);
                const effectiveFreeAmount = (freeBetAmount && this.getEffectiveStake(Number(freeBetAmount))) || stake;

                return this.roundNumber(decimalOdds.times(effectiveFreeAmount));
            }
        }

        return null;
    }

    getEffectiveStake(stake: number | null): number | null {
        if (!stake && stake !== 0) {
            return null;
        }

        return (this.isTurnoverTax ? this.roundNumber(new Decimal(1 - this.userSettings.configSettings.stakeTaxRate).times(stake)) : stake) as number;
    }

    getStakeTaxation(stake: Nullable<number>) {
        if (this.userSettings.configSettings.stakeTaxRate === 0 || !stake) {
            return null;
        }

        return Number(this.roundNumber(new Decimal(this.userSettings.configSettings.stakeTaxRate * stake)));
    }
}
